export interface ChannelStrip {
  index: number;
  label: string;
  /** Linear gain, 0..MAX_CHANNEL_GAIN (1 = unchanged). */
  gain: number;
  muted: boolean;
  /** Post-fader meter level, 0..100. */
  level: number;
}

export const MAX_CHANNEL_GAIN = 2;

/**
 * Converts a block of time-domain samples to a 0..100 meter value.
 * Shared by the overall recording meter and the per-channel meters so they read the same.
 */
export function computeMeterLevel(samples: Float32Array, noiseFloor: number): number {
  let sumSquares = 0;
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i];
    sumSquares += sample * sample;
    peak = Math.max(peak, Math.abs(sample));
  }

  const rms = Math.sqrt(sumSquares / samples.length);
  const signalLevel = Math.min(1, Math.max(0, (rms * 4.5) + (peak * 0.8)));
  const adjustedLevel = signalLevel <= noiseFloor ? 0 : (signalLevel - noiseFloor) / (1 - noiseFloor);
  return Math.min(100, Math.max(0, Math.round(adjustedLevel * 100)));
}

/** Labels for playback: speaker positions for mono/stereo, numbered channels otherwise. */
export function playbackChannelLabel(index: number, channelCount: number): string {
  if (channelCount === 1) {
    return 'Mono';
  }
  if (channelCount === 2) {
    return index === 0 ? 'Left' : 'Right';
  }
  return `Ch ${index + 1}`;
}

/**
 * Per-channel gain/mute stage:
 *
 *   source -> splitter -> [gain_i -> analyser_i] -> merger -> destination
 *
 * Recording: the source is the capture stream and the destination a MediaStreamDestination feeding
 * the recorder/LiveKit (owned by the caller so the VB-Cable stream can be mixed into it).
 * Playback: the source is the player's media element and the destination the speakers.
 */
export class ChannelMixer {
  strips: ChannelStrip[] = [];

  private source: AudioNode | null = null;
  private splitter: ChannelSplitterNode | null = null;
  private merger: ChannelMergerNode | null = null;
  private gains: GainNode[] = [];
  private analysers: AnalyserNode[] = [];
  private sampleBuffer = new Float32Array(1024);

  constructor(
    private readonly ctx: AudioContext,
    private readonly destination: AudioNode,
    private readonly labelFor: (index: number, channelCount: number) => string = index => `Ch ${index + 1}`
  ) { }

  /** Channel count reported by the capture track, falling back when the browser does not expose it. */
  static channelCountOf(stream: MediaStream, fallback: number): number {
    // channelCount is missing from this TypeScript version's MediaTrackSettings typings.
    const settings = stream.getAudioTracks()[0]?.getSettings() as any;
    return settings?.channelCount || fallback || 1;
  }

  /**
   * Routes a capture stream through the channel strips. Can be called again mid-recording
   * (e.g. after a device switch); gain/mute settings are kept per channel index.
   */
  setInput(stream: MediaStream, fallbackChannels: number): void {
    this.connectSource(this.ctx.createMediaStreamSource(stream), ChannelMixer.channelCountOf(stream, fallbackChannels));
  }

  /** Routes any source node through the strips, rebuilding them if the channel count changed. */
  connectSource(source: AudioNode, channelCount: number): void {
    if (this.source && this.source !== source) {
      this.source.disconnect();
    }
    if (channelCount !== this.strips.length) {
      this.source?.disconnect();
      this.buildStrips(channelCount);
    }
    this.source = source;
    this.source.connect(this.splitter!);
  }

  setGain(index: number, gain: number): void {
    const strip = this.strips[index];
    if (!strip) {
      return;
    }
    strip.gain = Math.min(MAX_CHANNEL_GAIN, Math.max(0, gain));
    this.applyGain(index);
  }

  setMuted(index: number, muted: boolean): void {
    const strip = this.strips[index];
    if (!strip) {
      return;
    }
    strip.muted = muted;
    this.applyGain(index);
  }

  /** Samples every channel's analyser; returns true if any displayed level changed. */
  updateLevels(noiseFloor: number): boolean {
    let changed = false;
    this.analysers.forEach((analyser, i) => {
      analyser.getFloatTimeDomainData(this.sampleBuffer);
      const level = computeMeterLevel(this.sampleBuffer, noiseFloor);
      if (level !== this.strips[i].level) {
        this.strips[i].level = level;
        changed = true;
      }
    });
    return changed;
  }

  clearLevels(): void {
    this.strips.forEach(strip => strip.level = 0);
  }

  dispose(): void {
    this.source?.disconnect();
    this.splitter?.disconnect();
    this.gains.forEach(gain => gain.disconnect());
    this.analysers.forEach(analyser => analyser.disconnect());
    this.merger?.disconnect();
    this.source = null;
    this.splitter = null;
    this.merger = null;
    this.gains = [];
    this.analysers = [];
    this.strips = [];
  }

  private buildStrips(channelCount: number): void {
    const previous = this.strips;
    this.splitter?.disconnect();
    this.gains.forEach(gain => gain.disconnect());
    this.analysers.forEach(analyser => analyser.disconnect());
    this.merger?.disconnect();

    this.splitter = this.ctx.createChannelSplitter(channelCount);
    this.merger = this.ctx.createChannelMerger(channelCount);
    this.gains = [];
    this.analysers = [];
    this.strips = [];

    for (let i = 0; i < channelCount; i++) {
      const gain = this.ctx.createGain();
      const analyser = this.ctx.createAnalyser();
      analyser.fftSize = this.sampleBuffer.length;
      analyser.smoothingTimeConstant = 0.2;

      this.splitter.connect(gain, i);
      gain.connect(this.merger, 0, i);
      gain.connect(analyser);

      this.gains.push(gain);
      this.analysers.push(analyser);
      this.strips.push({
        index: i,
        label: this.labelFor(i, channelCount),
        gain: previous[i]?.gain ?? 1,
        muted: previous[i]?.muted ?? false,
        level: 0
      });
      this.applyGain(i);
    }

    this.merger.connect(this.destination);
  }

  private applyGain(index: number): void {
    const strip = this.strips[index];
    const target = strip.muted ? 0 : strip.gain;
    // Short ramp avoids audible clicks when a fader jumps or a channel is muted.
    this.gains[index].gain.setTargetAtTime(target, this.ctx.currentTime, 0.015);
  }
}

/**
 * Per-channel control for a playback <audio> element. Routing an element through Web Audio is one-way
 * (once captured it only sounds through that context), so this is done lazily on first use and kept
 * until close() — call that only when the page is destroyed.
 */
export class PlaybackChannelMixer {
  mixer: ChannelMixer | null = null;

  private ctx: AudioContext | null = null;
  private source: MediaElementAudioSourceNode | null = null;
  private element: HTMLMediaElement | null = null;
  private channelCount = 2;

  /** Call from a user gesture (e.g. opening the channels menu) so the context is allowed to start. */
  attach(element: HTMLMediaElement): void {
    if (this.element !== element) {
      // The player element was re-created; the old one is gone, so start a fresh graph.
      this.close();
      this.ctx = new AudioContext();
      this.source = this.ctx.createMediaElementSource(element);
      this.mixer = new ChannelMixer(this.ctx, this.ctx.destination, playbackChannelLabel);
      this.mixer.connectSource(this.source, this.channelCount);
      this.element = element;
    }
    this.resume();
  }

  /** A suspended context silences a captured element, so call this before playing. */
  resume(): void {
    void this.ctx?.resume();
  }

  /** Channel count reported by the stream (e.g. hls.js BUFFER_CODECS); defaults to stereo until known. */
  setChannelCount(channelCount: number | undefined): void {
    if (!channelCount || channelCount === this.channelCount) {
      return;
    }
    this.channelCount = channelCount;
    if (this.mixer && this.source) {
      this.mixer.connectSource(this.source, channelCount);
    }
  }

  close(): void {
    this.mixer?.dispose();
    if (this.ctx && this.ctx.state !== 'closed') {
      void this.ctx.close();
    }
    this.mixer = null;
    this.ctx = null;
    this.source = null;
    this.element = null;
  }
}
